import React from 'react';
import { createRoot } from 'react-dom/client'
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";

// Error Boundary and Global Error Handling
import ErrorBoundary from "@/components/core/ErrorBoundary";
import { errorHandler } from "@/utils/errorHandler";
import { logger } from "@/utils/logger";

// Layouts
import GuestLayout from "./components/guest/GuestLayout";
import CustomerLayout from "./components/customer/CustomerLayout";
import AdminLayout from "./components/admin/AdminLayout";
import RiderLayout from "./components/rider/RiderLayout";

// Context Providers
import { AuthProvider } from "./contexts/AuthContext";
import { BranchProvider } from "./contexts/BranchContext";
import { ProductComparisonProvider } from "./contexts/ProductComparisonContext";
import { UIProvider } from "./contexts/UIContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import ProtectedRoute from "./components/core/ProtectedRoute";
import { ScrollToTop } from "./components/core/ScrollToTop";

// Lazy load components for better performance
const CelebrationHomePage = React.lazy(() => import("./pages/guest/CelebrationHomePage"));
const MenuPage = React.lazy(() => import("./pages/guest/MenuPage"));
const CelebrationCategoryPage = React.lazy(() => import("./pages/guest/CelebrationCategoryPage"));
const FullPriceListPage = React.lazy(() => import("./pages/guest/FullPriceListPage"));
const CelebrationAboutPage = React.lazy(() => import("./pages/guest/CelebrationAboutPage"));
const GalleryPage = React.lazy(() => import("./pages/guest/GalleryPage"));
const CelebrationContactPage = React.lazy(() => import("./pages/guest/CelebrationContactPage"));
const StopTextsPage = React.lazy(() => import("./pages/guest/StopTextsPage"));
const OrderConfirmationPage = React.lazy(() => import("./pages/guest/OrderConfirmationPage"));
const CorporatePackagesPage = React.lazy(() => import("./pages/guest/CorporatePackagesPage"));
const PersonalPackagesPage = React.lazy(() => import("./pages/guest/PersonalPackagesPage"));
const StudentPackagesPage = React.lazy(() => import("./pages/guest/StudentPackagesPage"));
const CorporateGiftingPage = React.lazy(() => import("./pages/guest/CorporateGiftingPage"));
const WhyCelebratePage = React.lazy(() => import("./pages/guest/WhyCelebratePage"));
const CelebrationHowItWorksPage = React.lazy(() => import("./pages/guest/CelebrationHowItWorksPage"));
const CelebrationRewardsPage = React.lazy(() => import("./pages/guest/CelebrationRewardsPage"));
const CelebrationWhatsAppPage = React.lazy(() => import("./pages/guest/CelebrationWhatsAppPage"));
const CelebrationReviewsPage = React.lazy(() => import("./pages/guest/CelebrationReviewsPage"));
const CorporateOrdersPage = React.lazy(() => import("./pages/guest/CorporateOrdersPage"));
const CustomPackagesPage = React.lazy(() => import("./pages/guest/CustomPackagesPage"));
const PackageDetailPage = React.lazy(() => import("./pages/guest/PackageDetailPage"));
const DevelopersPage = React.lazy(() => import("./pages/guest/DevelopersPage"));
const OnboardingLinkPage = React.lazy(() => import("./pages/guest/OnboardingLinkPage"));
const InvoicePage = React.lazy(() => import("./pages/guest/InvoicePage"));

// Blog System Pages
const BlogListPage = React.lazy(() => import("./pages/guest/BlogListPage"));
const BlogDetailPage = React.lazy(() => import("./pages/guest/BlogDetailPage"));
const BlogCategoryPage = React.lazy(() => import("./pages/guest/BlogCategoryPage"));

// Seasonal Landing Pages
const SeasonalLandingPage = React.lazy(() => import("./pages/guest/SeasonalLandingPage"));
const ResourcesPage = React.lazy(() => import("./pages/guest/ResourcesPage"));
const LeadMagnetPage = React.lazy(() => import("./pages/guest/LeadMagnetPage"));
const LeadMagnetDownloadPage = React.lazy(() => import("./pages/guest/LeadMagnetDownloadPage"));
const UnsubscribePage = React.lazy(() => import("./pages/guest/UnsubscribePage"));
const RateDeliveryPage = React.lazy(() => import("./pages/customer/RateDeliveryPage"));
const RateFoodPage = React.lazy(() => import("./pages/customer/RateFoodPage"));

// Essential components loaded immediately
import Index from "./pages/Index";
import NotFound from "./pages/guest/NotFound";
import AuthPage from "./pages/Auth";
import AuthRedirect from "./pages/AuthRedirect";
import ResetPassword from "./pages/ResetPassword";

// PWA Components
import { NotificationManager } from "./components/core/NotificationManager";
import { PWALoginShortcut, PWAProfileShortcut } from "./components/core/PWALoginShortcut";
import { PWAInstallPrompt } from "./components/core/PWAInstallPrompt";

// Payment callback redirect component (not lazy loaded for reliability)
import KowriCallbackRedirect from "./pages/KowriPaymentCallback";

// Lazy load customer pages
const OrderSummaryPage = React.lazy(() => import("./pages/customer/OrderSummaryPage"));
const PaymentSuccessPage = React.lazy(() => import("./pages/PaymentSuccessPage"));
const PaymentCancelledPage = React.lazy(() => import("./pages/PaymentCancelledPage"));
const PaymentErrorPage = React.lazy(() => import("./pages/PaymentErrorPage"));
const AccountPage = React.lazy(() => import("./pages/customer/Account"));
const WishlistPage = React.lazy(() => import("./pages/customer/Wishlist"));
const TermsConditionsPage = React.lazy(() => import("./pages/guest/TermsConditions"));
const PrivacyPolicyPage = React.lazy(() => import("./pages/guest/PrivacyPolicy"));
const CustomerInsightsPage = React.lazy(() => import("./pages/customer/InsightsPage"));
const CustomerEngagementPage = React.lazy(() => import("./pages/customer/AutomatedEngagementPage"));
const CustomerDashboard = React.lazy(() => import("./pages/customer/CustomerDashboard"));

// Lazy load admin pages
const AdminPage = React.lazy(() => import("./pages/admin/Admin"));
const AdminDashboardPage = React.lazy(() => import("./pages/admin/Dashboard"));
const DashboardDetailsPage = React.lazy(() => import("./pages/admin/DashboardDetails"));
const DiscountsPage = React.lazy(() => import("./pages/admin/Discounts"));
const PromotionalBannersPage = React.lazy(() => import("./pages/admin/PromotionalBannersPage"));
const KitchenOrdersDisplay = React.lazy(() => import("./components/kitchen/KitchenOrdersDisplay"));
const Inventory = React.lazy(() => import("./pages/admin/Inventory"));
const SettingsPage = React.lazy(() => import("./pages/admin/Settings"));
const RidersPage = React.lazy(() => import("./pages/admin/RidersPage"));
const CustomerServicePage = React.lazy(() => import("./pages/admin/CustomerService"));
const AdminRiderDashboardPage = React.lazy(() => import("./pages/admin/RiderDashboardPage"));
const PlaceOrderPage = React.lazy(() => import("./pages/admin/PlaceOrderPage"));
const CustomersPage = React.lazy(() => import("./pages/admin/CustomersPage"));
const PaymentManagementPage = React.lazy(() => import("./pages/admin/PaymentManagementPage"));
const AdminOrderDetailsPage = React.lazy(() => import("./pages/admin/AdminOrderDetails"));
const EnhancedAdminOrdersPage = React.lazy(() => import("./pages/admin/EnhancedAdminOrders"));
const CustomerServiceDashboard = React.lazy(() => import("./pages/admin/CustomerService"));
const DispatchManagementApp = React.lazy(() => import("./pages/admin/DispatchManagementApp"));
const DeliveryZonesPage = React.lazy(() => import("./pages/admin/DeliveryZonesPage"));
const DeliveryLocationsPage = React.lazy(() => import("./pages/admin/DeliveryLocationsPage"));
const ComprehensiveUserManagement = React.lazy(() => import("./pages/admin/ComprehensiveUserManagement"));
const CashTrackingPage = React.lazy(() => import("./pages/admin/CashTrackingPage"));
const DiscountDetailsPage = React.lazy(() => import("./pages/admin/DiscountDetailsPage"));
const CategoryAnalyticsPage = React.lazy(() => import("./pages/admin/CategoryAnalyticsPage"));
const AdvancedMetaSystemPage = React.lazy(() => import("./pages/admin/AdvancedMetaSystemPage"));
const SpecialDaysPage = React.lazy(() => import("./pages/admin/SpecialDaysPage"));
const PackageManagementPage = React.lazy(() => import("./pages/admin/EnhancedPackageManagementPage"));
const PackageCategoriesPage = React.lazy(() => import("./pages/admin/PackageCategoriesPage"));
const CustomerIntelligenceDashboard = React.lazy(() => import("./components/admin/CustomerIntelligenceDashboard"));
const PackageAnalyticsDashboard = React.lazy(() => import("./components/admin/PackageAnalyticsDashboard"));
const ProcurementDashboard = React.lazy(() => import("./components/admin/ProcurementDashboard"));
const ProductSpecificDiscountManager = React.lazy(() => import("./components/admin/ProductSpecificDiscountManager"));
const BulkLoyaltyDiscountSystem = React.lazy(() => import("./components/admin/BulkLoyaltyDiscountSystem"));
const AutomatedEngagementPage = React.lazy(() => import("./pages/admin/AutomatedEngagementPage"));
const PredictiveAnalyticsPage = React.lazy(() => import("./pages/admin/PredictiveAnalyticsPage"));
const ContentManagementPage = React.lazy(() => import("./pages/admin/ContentManagementPage"));
const CRMAutomationPage = React.lazy(() => import("./pages/admin/CRMAutomationPage"));
const WhatsAppBusinessPage = React.lazy(() => import("./pages/admin/WhatsAppBusinessPage"));
const StoreSettingsPage = React.lazy(() => import("./pages/admin/StoreSettingsPage"));
const NotificationSettingsPage = React.lazy(() => import("./pages/admin/NotificationSettingsPage"));
const SmartAnalyticsPage = React.lazy(() => import("./pages/admin/SmartAnalyticsPage"));
const SeasonalThemesPage = React.lazy(() => import("./pages/admin/SeasonalThemesPage"));
const LeadMagnetManagement = React.lazy(() => import("./pages/admin/LeadMagnetManagement"));
const LeadMagnetCreator = React.lazy(() => import("./pages/admin/LeadMagnetCreator"));
const BlogManagement = React.lazy(() => import("./pages/admin/BlogManagement"));
// Lazy load feedback and review pages
const FeedbackManagementPage = React.lazy(() => import("./pages/admin/FeedbackManagementPage"));
const ProductReviewsPage = React.lazy(() => import("./pages/admin/ProductReviewsPage"));
const AdminAnalyticsPage = React.lazy(() => import("./pages/admin/AdminAnalyticsPage"));
const ApiManagementPage = React.lazy(() => import("./pages/admin/ApiManagementPage"));
const TestServices = React.lazy(() => import("./pages/TestServices"));

// Lazy load rider pages
const RiderDashboardPage = React.lazy(() => import("./pages/rider/RiderDashboard"));
const RiderOrdersPage = React.lazy(() => import("./pages/rider/RiderOrders"));
const RiderProfilePage = React.lazy(() => import("./pages/rider/RiderProfile"));
const RiderSettingsPage = React.lazy(() => import("./pages/rider/RiderSettings"));
const RiderSelector = React.lazy(() => import("./components/admin/RiderSelector"));
const RiderDetailPage = React.lazy(() => import("./pages/admin/RiderDetailPage"));
const GranularAnalyticsDashboard = React.lazy(() => import("./pages/admin/GranularAnalyticsDashboard"));
const CashSubmissionManagement = React.lazy(() => import("./pages/admin/CashSubmissionManagement"));
const CustomerDetailPage = React.lazy(() => import("./pages/admin/CustomerDetailPage"));

// Cart transfer handler (not lazy loaded)
import CartTransferHandler from "./components/core/CartTransferHandler";
import RedirectHandler from "./components/core/RedirectHandler";
import ExternalOrderRedirect from "./components/core/ExternalOrderRedirect";

// ConsolidationDebugNav - removed from customer-facing pages (Issue #70)
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error: unknown) => {
        // Don't retry on 4xx errors except 408, 429
        if (error && typeof error === 'object' && 'status' in error) {
          const status = (error as { status: number }).status;
          if (status >= 400 && status < 500 && ![408, 429].includes(status)) {
            return false;
          }
        }
        return failureCount < 3;
      },
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes (fixed from cacheTime)
    },
    mutations: {
      retry: (failureCount, error: unknown) => {
        // Don't retry mutations on client errors
        if (error && typeof error === 'object' && 'status' in error) {
          const status = (error as { status: number }).status;
          if (status >= 400 && status < 500) {
            return false;
          }
        }
        return failureCount < 2;
      },
    },
  },
});

// Initialize global error handling
errorHandler.initialize();

// Loading component
const PageLoader = () => (
  <div className="flex items-center justify-center h-screen">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
  </div>
);

const App = () => {
  React.useEffect(() => {
    // Initialize performance monitoring
    // logger.info('Application started', {
    //   timestamp: new Date().toISOString(),
    //   userAgent: navigator.userAgent,
    //   url: window.location.href
    // });

    // Flush logs periodically
    const logFlushInterval = setInterval(() => {
      logger.flushLogs();
    }, 30000); // Every 30 seconds

    return () => {
      clearInterval(logFlushInterval);
      logger.flushLogs(); // Final flush
    };
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <TooltipProvider>
          <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
            <Toaster />
            <Sonner />
            <AuthProvider>
              <BranchProvider>
                <ProductComparisonProvider>
                  <UIProvider>
                    <ScrollToTop />
                    <CartTransferHandler />
                    <RedirectHandler />
                    <NotificationManager />
                    <PWALoginShortcut />
                    <PWAProfileShortcut />
                    <PWAInstallPrompt />
                    <ErrorBoundary>
                      <React.Suspense fallback={<PageLoader />}>
                        <Routes>
                          {/* Main Site Routes */}
                          <Route element={<GuestLayout />}>
                            <Route index element={<CelebrationHomePage />} />
                            <Route path="/menu" element={<MenuPage />} />
                            <Route path="/menu/:categorySlug" element={<CelebrationCategoryPage />} />
                            <Route path="/menu/:categorySlug/package/:packageId" element={<PackageDetailPage />} />
                            <Route path="/menu-old" element={<MenuPage />} />
                            <Route path="/full-pricelist" element={<FullPriceListPage />} />
                            <Route path="/about" element={<CelebrationAboutPage />} />
                            <Route path="/gallery" element={<GalleryPage />} />
                            <Route path="/contact" element={<CelebrationContactPage />} />
                            <Route path="/stop" element={<StopTextsPage />} />
                            <Route path="/cart" element={<ExternalOrderRedirect />} />
                            <Route path="/checkout" element={<ExternalOrderRedirect />} />
                            <Route path="/order-confirmation/:orderNumber" element={<OrderConfirmationPage />} />
                            <Route path="/products/:id" element={<ExternalOrderRedirect />} />
                            <Route path="/package/:packageId" element={<PackageDetailPage />} />
                            <Route path="/packages/corporate" element={<CorporatePackagesPage />} />
                            <Route path="/packages/personal" element={<PersonalPackagesPage />} />
                            <Route path="/packages/student" element={<StudentPackagesPage />} />
                            <Route path="/packages/corporate-gifting" element={<CorporateGiftingPage />} />
                            <Route path="/why-celebrate" element={<WhyCelebratePage />} />
                            <Route path="/how-it-works" element={<CelebrationHowItWorksPage />} />
                            <Route path="/rewards" element={<CelebrationRewardsPage />} />
                            <Route path="/whatsapp-ordering" element={<CelebrationWhatsAppPage />} />
                            <Route path="/reviews" element={<CelebrationReviewsPage />} />
                            <Route path="/resources" element={<ResourcesPage />} />
                            <Route path="/corporate-orders" element={<CorporateOrdersPage />} />
                            <Route path="/custom-packages" element={<CustomPackagesPage />} />
                            <Route path="/lead-magnet/:slug" element={<LeadMagnetPage />} />
                            <Route path="/download/:captureId" element={<LeadMagnetDownloadPage />} />
                            <Route path="/unsubscribe" element={<UnsubscribePage />} />
                            <Route path="/rate-delivery/:assignmentId" element={<RateDeliveryPage />} />
                            <Route path="/rate-food/:orderId" element={<RateFoodPage />} />
                            <Route path="/developers" element={<DevelopersPage />} />
                            <Route path="/developers/onboarding/:linkToken" element={<OnboardingLinkPage />} />
                            <Route path="/invoices/:invoiceToken" element={<InvoicePage />} />
                            
                            {/* Blog Routes */}
                            <Route path="/blog" element={<BlogListPage />} />
                            <Route path="/blog/:slug" element={<BlogDetailPage />} />
                            <Route path="/blog/category/:slug" element={<BlogCategoryPage />} />
                            
                            {/* Seasonal Holiday Landing Pages */}
                            <Route path="/christmas" element={<SeasonalLandingPage holiday="christmas" />} />
                            <Route path="/valentines" element={<SeasonalLandingPage holiday="valentines" />} />
                            <Route path="/mothers-day" element={<SeasonalLandingPage holiday="mothers-day" />} />
                            <Route path="/fathers-day" element={<SeasonalLandingPage holiday="fathers-day" />} />
                            <Route path="/independence-day" element={<SeasonalLandingPage holiday="independence-day" />} />
                          </Route>

                          {/* Kowri Payment Callback Route (WooCommerce-style endpoint) - Redirect to Edge Function */}
                          <Route path="/wc-api/WC_Slydepay_Payment_Gateway/*" element={<KowriCallbackRedirect />} />

                          {/* Public customer routes */}
                          <Route element={<CustomerLayout />}>
                            <Route path="/app" element={<Index />} />
                            <Route path="/auth" element={<AuthPage />} />
                            <Route path="/auth-redirect" element={<AuthRedirect />} />
                            <Route path="/reset-password" element={<ResetPassword />} />
                            <Route path="/test-services" element={<TestServices />} />
                            <Route path="/products" element={<ExternalOrderRedirect />} />
                            <Route path="/cart" element={<ExternalOrderRedirect />} />
                            <Route path="/kitchen" element={<KitchenOrdersDisplay />} />
                            <Route path="/checkout" element={<ExternalOrderRedirect />} />
                            <Route path="/product-comparison" element={<ExternalOrderRedirect />} />
                            <Route path="/order/:orderId/summary" element={<OrderSummaryPage />} />
                            <Route path="/pay/:orderId" element={<OrderSummaryPage />} />
                            <Route path="/payment-success" element={<PaymentSuccessPage />} />
                            <Route path="/payment-cancelled" element={<PaymentCancelledPage />} />
                            <Route path="/payment-error" element={<PaymentErrorPage />} />
                            <Route path="/terms" element={<TermsConditionsPage />} />
                            <Route path="/privacy" element={<PrivacyPolicyPage />} />
                          </Route>

                          {/* Protected customer routes */}
                          <Route element={<ProtectedRoute />}>
                            <Route element={<CustomerLayout />}>
                              <Route path="/dashboard" element={<CustomerDashboard />} />
                              <Route path="/orders" element={<ExternalOrderRedirect />} />
                              <Route path="/orders/:id" element={<ExternalOrderRedirect />} />
                              <Route path="/account" element={<AccountPage />} />
                              <Route path="/wishlist" element={<WishlistPage />} />
                              <Route path="/insights" element={<CustomerInsightsPage />} />
                              <Route path="/engagement" element={<CustomerEngagementPage />} />
                            </Route>
                          </Route>

                          {/* Protected admin routes */}
                          <Route element={<ProtectedRoute adminOnly />}>
                            <Route element={<AdminLayout />}>
                              <Route path="/admin" element={<AdminPage />} />
                              <Route path="/admin/dashboard" element={<AdminDashboardPage />} />
                              <Route path="/admin/dashboard-details/:metricType" element={<DashboardDetailsPage />} />
                              <Route path="/admin/products" element={<AdminPage />} />
                              <Route path="/admin/orders" element={<EnhancedAdminOrdersPage />} />
                              <Route path="/admin/orders/:id" element={<AdminOrderDetailsPage />} />
                              <Route path="/admin/customers" element={<CustomersPage />} />
                              <Route path="/admin/customers/:customerId" element={<CustomerDetailPage />} />
                              <Route path="/admin/customer-intelligence" element={<CustomerIntelligenceDashboard />} />
                              <Route path="/admin/place-order" element={<PlaceOrderPage />} />
                              <Route path="/admin/discounts" element={<DiscountsPage />} />
                              <Route path="/admin/discounts/:discountId" element={<DiscountDetailsPage />} />
                              <Route path="/admin/promotional-banners" element={<PromotionalBannersPage />} />
                              <Route path="/admin/analytics/category/:categoryName" element={<CategoryAnalyticsPage />} />
                              <Route path="/admin/analytics/granular" element={<GranularAnalyticsDashboard />} />
                              <Route path="/admin/meta-system" element={<AdvancedMetaSystemPage />} />
                              <Route path="/admin/special-days" element={<SpecialDaysPage />} />
                              <Route path="/admin/packages" element={<PackageManagementPage />} />
                              <Route path="/admin/package-categories" element={<PackageCategoriesPage />} />
                              <Route path="/admin/package-analytics" element={<PackageAnalyticsDashboard />} />
                              <Route path="/admin/procurement" element={<ProcurementDashboard />} />
                              <Route path="/admin/product-discounts" element={<ProductSpecificDiscountManager />} />
                              <Route path="/admin/bulk-loyalty" element={<BulkLoyaltyDiscountSystem />} />
                              <Route path="/admin/inventory" element={<Inventory />} />
                              <Route path="/admin/kitchen-display" element={<KitchenOrdersDisplay />} />
                              <Route path="/admin/riders" element={<RiderSelector />} />
                              <Route path="/admin/riders/:riderId" element={<RiderDetailPage />} />
                              <Route path="/admin/dispatcher" element={<DispatchManagementApp />} />
                              <Route path="/admin/dispatch-management" element={<DispatchManagementApp />} />
                              <Route path="/admin/delivery-zones" element={<DeliveryZonesPage />} />
                              <Route path="/admin/delivery-locations" element={<DeliveryLocationsPage />} />
                              <Route path="/admin/kitchen-orders-display" element={<KitchenOrdersDisplay />} />
                              <Route path="/admin/customer-service" element={<CustomerServicePage />} />
                              <Route path="/admin/customer-service-dashboard" element={<CustomerServiceDashboard />} />
                              <Route path="/admin/rider-dashboard" element={<AdminRiderDashboardPage />} />
                              <Route path="/admin/user-management" element={<ComprehensiveUserManagement />} />
                              <Route path="/admin/cash-tracking" element={<CashTrackingPage />} />
                              <Route path="/admin/cash-submissions" element={<CashSubmissionManagement />} />
                              <Route path="/admin/inventory" element={<Inventory />} />
                              <Route path="/admin/settings" element={<SettingsPage />} />
                              <Route path="/admin/payment-management" element={<PaymentManagementPage />} />
                              <Route path="/admin/automated-engagement" element={<AutomatedEngagementPage />} />
                              <Route path="/admin/predictive-analytics" element={<PredictiveAnalyticsPage />} />
                              <Route path="/admin/content-management" element={<ContentManagementPage />} />
                              <Route path="/admin/crm-automation" element={<CRMAutomationPage />} />
                              <Route path="/admin/whatsapp-business" element={<WhatsAppBusinessPage />} />
                              <Route path="/admin/store-settings" element={<StoreSettingsPage />} />
                              <Route path="/admin/smart-analytics" element={<SmartAnalyticsPage />} />
                              <Route path="/admin/seasonal-themes" element={<SeasonalThemesPage />} />
                              <Route path="/admin/lead-magnets" element={<LeadMagnetManagement />} />
                              <Route path="/admin/lead-magnets/create" element={<LeadMagnetCreator />} />
                              <Route path="/admin/lead-magnets/edit/:id" element={<LeadMagnetCreator />} />
                              <Route path="/admin/notification-settings" element={<NotificationSettingsPage />} />
                              <Route path="/admin/blog-management" element={<BlogManagement />} />
                              <Route path="/admin/admin-analytics" element={<AdminAnalyticsPage />} />
                              <Route path="/admin/feedback-management" element={<FeedbackManagementPage />} />
                              <Route path="/admin/product-reviews" element={<ProductReviewsPage />} />
                              <Route path="/admin/api-management" element={<ApiManagementPage />} />
                              <Route path="/admin/api-management/customers" element={<ApiManagementPage />} />
                              <Route path="/admin/api-management/tokens" element={<ApiManagementPage />} />
                              <Route path="/admin/api-management/rate-limits" element={<ApiManagementPage />} />
                              <Route path="/admin/api-management/analytics" element={<ApiManagementPage />} />
                              {/* Admin view of rider dashboard - allows viewing as any rider */}
                              <Route path="/admin/rider-view" element={<RiderSelector />} />
                              <Route path="/admin/rider-view/:riderId" element={<RiderDashboardPage />} />
                            </Route>
                          </Route>

                          {/* Protected rider routes */}
                          <Route element={<ProtectedRoute riderOnly />}>
                            <Route element={<RiderLayout />}>
                              <Route path="/rider/dashboard" element={<RiderDashboardPage />} />
                              <Route path="/rider/orders" element={<RiderOrdersPage />} />
                              <Route path="/rider/profile" element={<RiderProfilePage />} />
                              <Route path="/rider/settings" element={<RiderSettingsPage />} />
                            </Route>
                          </Route>

                          {/* Catch-all route */}
                          <Route path="*" element={<NotFound />} />
                        </Routes>
                      </React.Suspense>
                    </ErrorBoundary>
                  </UIProvider>
                </ProductComparisonProvider>
              </BranchProvider>
            </AuthProvider>
          </BrowserRouter>
        </TooltipProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
};

export default App;

